import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered,
  Quote, Link as LinkIcon, Image as ImgIcon, Undo, Redo, Loader2, Minus,
} from "lucide-react";
import { API } from "@/lib/brand";
import { toast } from "sonner";

const btn =
  "inline-flex items-center justify-center w-9 h-9 rounded-md border hairline text-ink hover:bg-paper-raised transition-colors";
const btnActive =
  "inline-flex items-center justify-center w-9 h-9 rounded-md border border-ink bg-paper-raised text-ink";

export default function TipTapEditor({ value, onChange }) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder: "Start writing your post…" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Keep external value in sync if it changes (e.g. loading an existing post)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== undefined && value !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const setLink = () => {
    const prev = editor?.getAttributes("link").href || "";
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const uploadImage = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const t = toast.loading("Uploading image…");
    try {
      const res = await fetch(`${API}/journal/admin/upload`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Upload failed");
      }
      const data = await res.json();
      const abs = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      editor.chain().focus().setImage({ src: abs, alt: file.name }).run();
      toast.success("Image inserted", { id: t });
    } catch (e) {
      toast.error(e.message || "Upload failed", { id: t });
    }
  };

  if (!editor) return null;

  return (
    <div className="border hairline rounded-md overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b hairline bg-paper-raised" data-testid="editor-toolbar">
        <ToolBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold"><Bold size={16} /></ToolBtn>
        <ToolBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic"><Italic size={16} /></ToolBtn>
        <ToolBtn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} label="Strikethrough"><Strikethrough size={16} /></ToolBtn>
        <span className="mx-1 w-px h-6 bg-line" />
        <ToolBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} label="Heading 2"><Heading2 size={16} /></ToolBtn>
        <ToolBtn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} label="Heading 3"><Heading3 size={16} /></ToolBtn>
        <span className="mx-1 w-px h-6 bg-line" />
        <ToolBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bulleted list"><List size={16} /></ToolBtn>
        <ToolBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Numbered list"><ListOrdered size={16} /></ToolBtn>
        <ToolBtn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} label="Blockquote"><Quote size={16} /></ToolBtn>
        <ToolBtn active={false} onClick={() => editor.chain().focus().setHorizontalRule().run()} label="Divider"><Minus size={16} /></ToolBtn>
        <span className="mx-1 w-px h-6 bg-line" />
        <ToolBtn active={editor.isActive("link")} onClick={setLink} label="Link"><LinkIcon size={16} /></ToolBtn>
        <ToolBtn active={false} onClick={() => fileInputRef.current?.click()} label="Insert image"><ImgIcon size={16} /></ToolBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadImage(f);
            e.target.value = "";
          }}
          data-testid="editor-image-input"
        />
        <span className="mx-1 w-px h-6 bg-line" />
        <ToolBtn active={false} onClick={() => editor.chain().focus().undo().run()} label="Undo"><Undo size={16} /></ToolBtn>
        <ToolBtn active={false} onClick={() => editor.chain().focus().redo().run()} label="Redo"><Redo size={16} /></ToolBtn>
      </div>
      <EditorContent
        editor={editor}
        className="prose-editor px-5 py-4 min-h-[420px] focus-within:outline-none"
        data-testid="editor-content"
      />
    </div>
  );
}

function ToolBtn({ active, onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={active ? btnActive : btn}
      title={label}
      aria-label={label}
    >
      {children}
    </button>
  );
}
